import React from 'react';
import {
    Box,
    Button,
    Badge,
    FormControl,
    FormLabel,
    Input,
    Flex,
    SimpleGrid,
    IconButton,
    Tooltip,
    Image,
    Switch,
    Text,
    useColorModeValue,
} from '@chakra-ui/react';
import { DeleteIcon } from '@chakra-ui/icons';

const Step2Options = ({ options, setOptions, useOptionId }) => {
    const cardBg = useColorModeValue('white', 'navy.800');
    const subtleBg = useColorModeValue('gray.50', 'whiteAlpha.100');
    const titleColor = useColorModeValue('gray.700', 'gray.200');

    const handleOptionChange = (index, field, value) => {
        const newOptions = [...options];
        newOptions[index][field] = value;

        if (field === 'quantity' && useOptionId) {
            const quantity = parseInt(value, 10);
            newOptions[index].productIds = new Array(quantity || 1).fill('');
        }

        setOptions(newOptions);
    };

    const handleProductIdChange = (optionIndex, productIndex, value) => {
        const newOptions = [...options];
        newOptions[optionIndex].productIds[productIndex] = value;
        setOptions(newOptions);
    };

    const handleImageChange = (index, event) => {
        const file = event.target.files[0];
        const newOptions = [...options];

        if (file) {
            newOptions[index].image = file;
            newOptions[index].imageUrl = URL.createObjectURL(file);
        } else {
            newOptions[index].image = null;
            newOptions[index].imageUrl = '';
        }

        setOptions(newOptions);
    };

    const addOption = () => {
        setOptions([
            ...options,
            {
                name: '',
                value: '',
                quantity: '',
                catch_phrase: '',
                image: null,
                imageUrl: '',
                reduced_price: '',
                productIds: [''],
                preselected: false,
                free_shipping: false,
            },
        ]);
    };

    const removeOption = (index) => {
        const newOptions = options.filter((_, i) => i !== index);
        setOptions(newOptions);
    };

    return (
        <Box>
            {options.map((option, index) => (
                <Box key={index} mb="6" p={{ base: 4, md: 5 }} borderWidth="1px" borderRadius="2xl" boxShadow="sm" bg={cardBg}>
                    <Flex justify="space-between" align={{ base: 'flex-start', md: 'center' }} gap="4" mb="5" direction={{ base: 'column', md: 'row' }}>
                        <Box>
                            <Badge colorScheme="blue" borderRadius="full" mb="2">
                                Option {index + 1}
                            </Badge>
                            <Text fontWeight="700" color={titleColor}>
                                {option.name || 'Untitled option'}
                            </Text>
                            <Text fontSize="sm" color="gray.500">
                                Set the customer-facing choice, price and optional merchandising details.
                            </Text>
                        </Box>
                        <Tooltip label="Delete Option">
                            <IconButton
                                icon={<DeleteIcon />}
                                onClick={() => removeOption(index)}
                                colorScheme="red"
                                variant="ghost"
                                aria-label="delete option"
                            />
                        </Tooltip>
                    </Flex>

                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing="4" mb="4">
                        <FormControl>
                            <FormLabel>Name</FormLabel>
                            <Input
                                value={option.name}
                                placeholder="e.g. 1 piece"
                                onChange={(e) => handleOptionChange(index, 'name', e.target.value)}
                            />
                        </FormControl>
                        <FormControl>
                            <FormLabel>Value</FormLabel>
                            <Input
                                value={option.value}
                                placeholder="Regular price"
                                onChange={(e) => handleOptionChange(index, 'value', e.target.value)}
                            />
                        </FormControl>
                        <FormControl>
                            <FormLabel>Quantity</FormLabel>
                            <Input
                                type="number"
                                value={option.quantity}
                                placeholder="Quantity"
                                onChange={(e) => handleOptionChange(index, 'quantity', e.target.value)}
                            />
                        </FormControl>
                    </SimpleGrid>

                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing="4" mb="4">
                        <FormControl>
                            <FormLabel>Catch Phrase</FormLabel>
                            <Input
                                value={option.catch_phrase}
                                placeholder="Short sales line"
                                onChange={(e) => handleOptionChange(index, 'catch_phrase', e.target.value)}
                            />
                        </FormControl>
                        <FormControl>
                            <FormLabel>Reduced Price</FormLabel>
                            <Input
                                type="number"
                                value={option.reduced_price}
                                placeholder="Optional sale price"
                                onChange={(e) => handleOptionChange(index, 'reduced_price', e.target.value)}
                            />
                        </FormControl>
                        <FormControl>
                            <FormLabel>Upload Image</FormLabel>
                            <Input
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleImageChange(index, e)}
                            />
                            {option.imageUrl && (
                                <Box position="relative" mt="4">
                                    <Image
                                        src={option.imageUrl}
                                        alt={`Option ${index + 1}`}
                                        boxSize="100px"
                                        objectFit="cover"
                                        rounded="md"
                                    />
                                    <IconButton
                                        icon={<DeleteIcon />}
                                        aria-label="delete image"
                                        colorScheme="red"
                                        position="absolute"
                                        top="0"
                                        right="0"
                                        onClick={() => handleImageChange(index, { target: { files: [null] } })}
                                    />
                                </Box>
                            )}
                        </FormControl>
                    </SimpleGrid>

                    {useOptionId && (
                        <Box p="4" bg={subtleBg} borderRadius="xl" mb="4">
                            <Text fontWeight="700" mb="3" color={titleColor}>
                                WooCommerce IDs for this option
                            </Text>
                            <SimpleGrid columns={{ base: 1, md: 2 }} spacing="4">
                                {option.productIds.map((productId, productIndex) => (
                                    <FormControl key={productIndex}>
                                        <FormLabel>Product ID {productIndex + 1}</FormLabel>
                                        <Input
                                            value={productId}
                                            onChange={(e) => handleProductIdChange(index, productIndex, e.target.value)}
                                            placeholder="WooCommerce product ID"
                                        />
                                    </FormControl>
                                ))}
                            </SimpleGrid>
                        </Box>
                    )}

                    <Flex alignItems={{ base: 'flex-start', md: 'center' }} mt="4" gap="4" direction={{ base: 'column', md: 'row' }}>
                        <FormControl display="flex" alignItems="center" mb="0">
                            <Switch
                                id={`preselected-${index}`}
                                isChecked={option.preselected}
                                onChange={(e) => handleOptionChange(index, 'preselected', e.target.checked)}
                            />
                            <FormLabel htmlFor={`preselected-${index}`} mb="0" ml="2">
                                Preselected
                            </FormLabel>
                        </FormControl>
                        <FormControl display="flex" alignItems="center" mb="0">
                            <Switch
                                id={`free-shipping-${index}`}
                                isChecked={option.free_shipping}
                                onChange={(e) => handleOptionChange(index, 'free_shipping', e.target.checked)}
                            />
                            <FormLabel htmlFor={`free-shipping-${index}`} mb="0" ml="2">
                                Free Shipping
                            </FormLabel>
                        </FormControl>
                    </Flex>
                </Box>
            ))}
            <Button colorScheme="blue" onClick={addOption} borderRadius="xl">
                Add Option
            </Button>
        </Box>
    );
};

export default Step2Options;